import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { acceptInvite, type InviteView, readInvite } from '../api.ts';
import { LocalePicker } from '../components/LocalePicker.tsx';
import { ThemePicker } from '../components/ThemePicker.tsx';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { useLoad } from '../hooks/useLoad.ts';
import { errorMessage } from '../messages.ts';

/**
 * Accettare un invito (mock 5, `o-invite`).
 *
 * È la schermata da cui entra il **primo coordinatore** di un ente nuovo, e per
 * molti enti è la prima cosa che vedono del prodotto: "il presidente la apre
 * dal telefono mentre è in riunione di direttivo".
 *
 * Tre cose che fa, e nessuna è arbitraria:
 *
 *  - **chiede una password, non la dà.** Nessun ruolo ne imposta mai una per un
 *    altro (mock 5, `o-step4`), e lunga batte complicata: i volontari hanno in
 *    media 45-65 anni e una regola su maiuscole e simboli produce password
 *    scritte su un foglietto;
 *  - **non fa entrare.** Finita l'accettazione si va alla schermata di accesso,
 *    dove il secondo fattore aspetta chi coordina: "un coordinator senza TOTP
 *    configurato è obbligato a configurarlo al primo accesso" (G4);
 *  - **dice a quale ente si sta entrando**, prima di chiedere qualunque cosa.
 *
 * Il token sta nel frammento dell'indirizzo e non lo scrive da nessuna parte:
 * un frammento non viaggia verso nessun server (AK3).
 */

type Props = {
  token: string;
  /** Finito: si torna all'accesso, che è dove il secondo fattore aspetta. */
  onDone: () => void;
};

export function InvitePage({ token, onDone }: Props) {
  const { t } = useTranslation();
  const { say, announcer } = useAnnounce();
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  /**
   * "Ho completato la formazione all'ascolto" (mock 2, `st-invite`).
   *
   * Autodichiarata qui, come il protocollo di crisi — che però è un obbligo a
   * parte, versionato e riverificato in console a ogni aggiornamento del
   * documento (`protocolAck`, sezione 5.9): la formazione non cambia nel
   * tempo come un testo, è un fatto avvenuto una volta, quindi resta
   * un'attestazione singola presa qui e non un secondo gate in console.
   * Obbligatoria quanto il resto del modulo: non si può accettare l'invito
   * dichiarando di non averla completata.
   */
  const [trainingCompleted, setTrainingCompleted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const read = useCallback((signal: AbortSignal) => readInvite(token, { signal }), [token]);
  const { data: invite, failed } = useLoad<InviteView>(read, {
    onError: (error) => say(errorMessage(t, error), 'danger'),
  });

  /**
   * Il fuoco segue il cambio di schermata, non solo il montaggio.
   *
   * Accettando, l'intera card viene sostituita e il pulsante di invio sparisce:
   * senza `accepted` fra le dipendenze il fuoco cadeva sul corpo del documento,
   * il Tab ripartiva dall'inizio e lo screen reader taceva (WCAG 2.4.3, AM4).
   */
  /**
   * Un `ref` di funzione, non un effetto: si esegue quando il titolo **compare**.
   *
   * È esattamente ciò che serve — accettando, l'intera card viene sostituita, il
   * pulsante di invio sparisce e il fuoco cadeva sul corpo del documento
   * (WCAG 2.4.3, AM4) — e vale per entrambe le schermate senza che nessuna
   * lista di dipendenze debba ricordarsi di dirlo.
   */
  const focusOnAppear = useCallback((node: HTMLHeadingElement | null) => {
    node?.focus();
  }, []);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (busy || !invite) return;
    setBusy(true);
    try {
      await acceptInvite(token, {
        name: name.trim(),
        displayName: displayName.trim(),
        password,
        trainingCompleted,
      });
      setAccepted(true);
      say(t('invite.accepted'));
    } catch (error) {
      say(`${t('invite.failed')} ${errorMessage(t, error)}`, 'danger');
    } finally {
      setBusy(false);
    }
  };

  if (accepted) {
    return (
      <main className="page page--narrow" id="main">
        <div className="card">
          <h1 ref={focusOnAppear} tabIndex={-1}>
            {t('invite.done_title')}
          </h1>
          {announcer}
          <p className="muted">{t('invite.done_hint', { email: invite?.email ?? '' })}</p>
          {/*
            "Il secondo fattore lo configura al primo accesso" (G4): non è un
            passaggio in più, è il passaggio che rende quell'accesso un accesso
            da coordinatore.
          */}
          <p className="muted">{t('invite.done_totp')}</p>
          <button type="button" className="button button--primary" onClick={onDone}>
            {t('invite.go_to_login')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="page page--narrow" id="main">
      <div className="card">
        {/*
          Il titolo da solo, come nell'accesso: il selettore della lingua stava
          qui accanto, alla stessa altezza. Sta sotto, insieme all'aspetto —
          che su questa schermata mancava del tutto, mentre sull'accesso c'era:
          due schermate della stessa famiglia offrivano due cose diverse, e la
          differenza non aveva nessuna ragione.
        */}
        <h1 ref={focusOnAppear} tabIndex={-1}>
          {t('invite.title')}
        </h1>

        {announcer}

        {/* Con l'invito scaduto resta l'errore, non "Caricamento…" accanto. */}
        {!invite && !failed ? <p className="muted">{t('admin.loading')}</p> : null}

        {invite ? (
          <form onSubmit={(event) => void submit(event)} noValidate>
            <p className="muted">
              {/*
                Senza il nome del ruolo: `platform.role_*` nasce per gli elenchi
                interni del fornitore, dove il maschile passa. Qui parla a chi
                sta scegliendo la propria password, e il progetto scrive
                "Volontaria o volontario" ovunque si rivolga a una persona.
              */}
              {t('invite.subtitle', {
                tenant: invite.tenant.name,
                email: invite.email,
              })}
            </p>

            <div className="field">
              <label htmlFor="invite-name">{t('invite.name_label')}</label>
              <input
                id="invite-name"
                autoComplete="name"
                required
                maxLength={120}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="invite-display-name">{t('invite.display_name_label')}</label>
              <input
                id="invite-display-name"
                autoComplete="nickname"
                required
                maxLength={60}
                value={displayName}
                aria-describedby="invite-display-name-hint"
                onChange={(event) => setDisplayName(event.target.value)}
              />
              {/*
                Il nome pubblico è quello che l'utenza legge in chat: non è il
                nome anagrafico, e chi risponde deve poter scegliere quanto di
                sé mostrare.
              */}
              <p className="muted" id="invite-display-name-hint">
                {t('invite.display_name_hint')}
              </p>
            </div>

            <div className="field">
              <label htmlFor="invite-password">{t('invite.password_label')}</label>
              <input
                id="invite-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                maxLength={200}
                value={password}
                aria-describedby="invite-password-hint"
                onChange={(event) => setPassword(event.target.value)}
              />
              <p className="muted" id="invite-password-hint">
                {t('invite.password_hint')}
              </p>
            </div>

            <div className="field field--inline field--check">
              <input
                type="checkbox"
                id="invite-training"
                required
                checked={trainingCompleted}
                onChange={(event) => setTrainingCompleted(event.target.checked)}
              />
              <label htmlFor="invite-training">{t('invite.training_label')}</label>
            </div>

            <button
              type="submit"
              className="button button--primary"
              disabled={
                busy ||
                password.length < 12 ||
                !name.trim() ||
                !displayName.trim() ||
                !trainingCompleted
              }
            >
              {t('invite.submit')}
            </button>
          </form>
        ) : null}
      </div>

      {/*
        Lingua e aspetto, sotto la scheda: qui non c'è ancora nessun profilo su
        cui salvarle, quindi la scelta vale per questo dispositivo. È la stessa
        riga della schermata di accesso, allo stesso posto — chi arriva da un
        invito e poi entra non deve cercarla due volte in due punti diversi.
      */}
      <div className="prefs-bar">
        <LocalePicker />
        <ThemePicker />
      </div>
    </main>
  );
}
